import * as React from 'react';
import { cn } from '@/shared/utils/cn';
import { Button } from '@/shared/widgets/button';
import { useToast } from '@/shared/widgets/toast/useToast';
import { subscribeToNewsletter } from '@/shared/features/newsletter/application/services/newsletterService';

export interface NewsletterSubscribeProps
  extends Omit<React.HTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  placeholder?: string;
  buttonText?: string;
  onSubmit?: (email: string) => void;
  className?: string;
}

const NewsletterSubscribe = React.forwardRef<
  HTMLFormElement,
  NewsletterSubscribeProps
>(
  (
    {
      placeholder = 'Enter email for our newsletter',
      buttonText = 'SUBSCRIBE',
      onSubmit,
      className,
      ...props
    },
    ref
  ) => {
    const [email, setEmail] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const { toast } = useToast();
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedEmail = email.trim();
      if (!trimmedEmail || isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      try {
        await subscribeToNewsletter(trimmedEmail);

        if (onSubmit) {
          onSubmit(trimmedEmail);
        }

        setEmail('');
        toast({
          variant: 'success',
          title: 'Subscribed',
          description: 'You have been subscribed to the newsletter.',
        });
      } catch (error) {
         
        console.error('Failed to subscribe to newsletter', error);
        toast({
          variant: 'destructive',
          title: 'Subscription failed',
          description: 'Something went wrong. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div>
        <form
          ref={ref}
          onSubmit={handleSubmit}
          className={cn('flex w-full rounded-md overflow-hidden', className)}
          {...props}
        >
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            className="flex-[2] h-10 px-4 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus-visible:outline-none focus-visible:ring-0 border-0 rounded-l-md rounded-r-none"
            required
          />
        <Button
          type="submit"
          isLoading={isSubmitting}
          className={cn(
            'flex-[0.8] h-10 px-4 w-[140px] rounded-r-md rounded-l-none bg-white text-[#001e38] text-sm font-medium border-0',
            !isSubmitting && 'hover:bg-gray-100'
          )}
        >
          {buttonText}
        </Button>
        </form>

      </div>
    );
  }
);

NewsletterSubscribe.displayName = 'NewsletterSubscribe';

export { NewsletterSubscribe };

